import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import {
	Alert,
	AlertTitle,
	AppBar,
	Button,
	Checkbox,
	CircularProgress,
	FormControl,
	FormGroup,
	FormHelperText,
	IconButton,
	InputAdornment,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Paper,
	Toolbar,
	Typography,
} from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import Cookies from 'js-cookie';
import React, { useCallback, useEffect, useRef } from 'react';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { searchLocationsFormValidationSchema } from '../../app/forms/searchLocationsForm';
import { useAppDispatch, useAppSelector } from '../../app/hooks/store';
import { useFetchMapboxGeocodingResultsQuery } from '../../app/services/mapbox';
import { DialogWithTransition } from '../../app/ui/dialog';
import TextFieldWithPasteAdornment from '../../app/ui/textFieldWithPasteAdornment';
import {
	type SearchLocationsParameters,
	defaultSearchLocationsParameters,
	getSearchLocationsParameters,
	setSearchLocationsParameters,
	setSearchLocationsZoomToCoordinates,
} from '../app/appSlice';
import {
	type IMapboxGeocodingAPIResponseFeature,
	defaultMapboxSearchTypes,
	getMapboxAPIKey,
	isSearchingYet,
} from './searchLocationsHelpers';

interface FormProps extends SearchLocationsParameters {
	proximity: boolean;
}

function SearchLocationsManager() {
	const navigate = useNavigate();

	const dispatch = useAppDispatch();

	const searchParameters = useAppSelector(getSearchLocationsParameters);

	// ######################
	// Form Component
	// ######################
	const {
		watch,
		handleSubmit,
		setValue,
		reset,
		control,
		formState: { errors },
	} = useForm<FormProps>({
		resolver: yupResolver(searchLocationsFormValidationSchema),
		defaultValues: {
			search_term: searchParameters.search_term,
			proximity: Cookies.get('locationSearchProximity') === 'true',
		},
	});

	const { search_term, proximity } = watch();

	const onDoneWithForm: SubmitHandler<SearchLocationsParameters> = () => {};

	useEffect(() => {
		if (isSearchingYet(search_term) === true) {
			dispatch(setSearchLocationsParameters({ search_term }));
		}
	}, [dispatch, search_term]);
	// ######################
	// Form Component (End)
	// ######################

	// ######################
	// TextField Component
	// ######################
	const textInput = useRef<HTMLInputElement>(null);

	const onPasteFromClipboard = (pastedText: string) => {
		setValue('search_term', pastedText, { shouldDirty: true });
	};

	const onClickProximity = () => {
		setValue('proximity', !proximity, { shouldDirty: true });

		Cookies.set('locationSearchProximity', `${!proximity}`, {
			expires: 400, // This is the new maximum expiry for cookies in days
		});
	};
	// ######################
	// TextField Component (End)
	// ######################

	// ######################
	// Mapbox Search Query
	// ######################
	const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${search_term}.json?limit=10&proximity=${proximity === true ? 'ip' : ''}&types=${defaultMapboxSearchTypes.join(
		'%2C',
	)}&access_token=${getMapboxAPIKey()}`;

	const {
		data: mapboxSearchResults,
		error: errorFetchingMapboxResults,
		isFetching: isFetchingMapboxResults,
		isSuccess: isSuccessFetchingMapboxResults,
	} = useFetchMapboxGeocodingResultsQuery(isSearchingYet(search_term) === true ? { url } : skipToken);

	const onChoose = useCallback(
		(feature: IMapboxGeocodingAPIResponseFeature) => () => {
			dispatch(setSearchLocationsParameters({ search_term }));
			dispatch(setSearchLocationsZoomToCoordinates(feature.geometry.coordinates));
			navigate('/');
		},
		[dispatch, navigate, search_term],
	);
	// ######################
	// Mapbox Search Query (End)
	// ######################

	// ######################
	// Header Controls
	// ######################
	const onClose = () => navigate('/');

	const onClearSearchResults = () => {
		reset(defaultSearchLocationsParameters);
		dispatch(setSearchLocationsParameters(defaultSearchLocationsParameters));

		if (textInput.current !== null) {
			textInput.current.focus();
		}
	};
	// ######################
	// Header Controls (End)
	// ######################

	return (
		<React.Fragment>
			<DialogWithTransition onClose={onClose} disableRestoreFocus>
				<AppBar color="secondary" sx={{ position: 'sticky' }}>
					<Toolbar>
						<IconButton edge="start" color="inherit" onClick={onClose}>
							<CloseIcon />
						</IconButton>
						<Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
							Location Search
						</Typography>
						<Button color="inherit" onClick={onClearSearchResults}>
							Clear
						</Button>
					</Toolbar>
				</AppBar>

				<form onSubmit={handleSubmit(onDoneWithForm)}>
					<Paper elevation={0} sx={{ m: 3 }}>
						<FormControl fullWidth={true} sx={{ mb: 1 }} component="fieldset" variant="outlined">
							<FormGroup>
								<Controller
									name="search_term"
									control={control}
									render={({ field }) => (
										<TextFieldWithPasteAdornment
											{...field}
											inputRef={textInput}
											label="Search"
											autoFocus
											InputProps={{
												startAdornment: (
													<InputAdornment position="start">
														{isFetchingMapboxResults === true ? <CircularProgress size={24} /> : <SearchIcon />}
													</InputAdornment>
												),
											}}
											onPasteFromClipboard={onPasteFromClipboard}
										/>
									)}
								/>
							</FormGroup>

							{errors.search_term && <FormHelperText error>{errors.search_term.message}</FormHelperText>}
						</FormControl>

						<FormControl fullWidth={true} sx={{ mb: 2 }} component="fieldset" variant="outlined">
							<FormGroup>
								<List disablePadding>
									<ListItem disablePadding disableGutters>
										<ListItemButton dense onClick={onClickProximity}>
											<ListItemIcon>
												<Controller
													name="proximity"
													control={control}
													defaultValue={false}
													render={({ field }) => <Checkbox {...field} checked={field.value} />}
												/>
											</ListItemIcon>
											<ListItemText
												primary="Prefer results close to my location"
												secondary="(Based on your IP address.)"
												sx={{ '& .MuiListItemText-primary': { fontWeight: 500 } }}
											/>
										</ListItemButton>
									</ListItem>
								</List>
							</FormGroup>

							{errors.proximity && <FormHelperText error>{errors.proximity.message}</FormHelperText>}
						</FormControl>

						{/* Handles not found and all other types of error */}
						{errorFetchingMapboxResults !== undefined && (
							<Alert severity="error">
								<AlertTitle>Sorry, we&lsquo;ve hit a snag</AlertTitle>
								Something went awry when we tried to search for that place.
							</Alert>
						)}

						{isFetchingMapboxResults === false &&
							isSuccessFetchingMapboxResults === true &&
							mapboxSearchResults !== undefined && (
								<List sx={{ pt: 0 }}>
									{mapboxSearchResults === null && (
										<ListItem>
											<ListItemText primary="An error occurred" />
										</ListItem>
									)}

									{typeof mapboxSearchResults === 'object' &&
										mapboxSearchResults !== null &&
										mapboxSearchResults.features.length === 0 && (
											<ListItem>
												<ListItemText primary="No results found" />
											</ListItem>
										)}

									{isSearchingYet(search_term) === true &&
										typeof mapboxSearchResults === 'object' &&
										mapboxSearchResults !== null &&
										mapboxSearchResults.features.length > 0 &&
										mapboxSearchResults.features.map((feature) => {
											const [place_name_first_part, ...place_name_rest] = feature.place_name.split(', ');
											const matches = match(place_name_first_part, search_term, {
												insideWords: true,
											});
											const parts = parse(place_name_first_part, matches);

											return (
												<ListItem key={feature.id} onClick={onChoose(feature)} sx={{ cursor: 'pointer' }}>
													<ListItemText
														primary={
															<span>
																{parts.map((part, index) => (
																	<span
																		// biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
																		key={index}
																		style={{
																			fontWeight: part.highlight ? 700 : 400,
																		}}
																	>
																		{part.text}
																	</span>
																))}
															</span>
														}
														secondary={place_name_rest.join(', ')}
													/>
												</ListItem>
											);
										})}
								</List>
							)}
					</Paper>
				</form>
			</DialogWithTransition>
		</React.Fragment>
	);
}

export default SearchLocationsManager;
